import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Pulse, Clock, Users, FloppyDisk } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";

const REFRESH_MS = 15000;

function fmt(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso);
    return t.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function ago(iso) {
  if (!iso) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ago`;
}

export default function ActivityPanel() {
  const [live, setLive] = useState(null);
  const [daily, setDaily] = useState(null);
  const [retentionDays, setRetentionDays] = useState(5);
  const [editRetention, setEditRetention] = useState(5);
  const [savingRetention, setSavingRetention] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [l, d, s] = await Promise.all([
        api.get("/admin/activity/live?minutes=2"),
        api.get("/admin/activity/daily"),
        api.get("/admin/activity/settings"),
      ]);
      setLive(l.data);
      setDaily(d.data);
      setRetentionDays(s.data.activity_retention_days);
      setEditRetention(s.data.activity_retention_days);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    reload();
    const t = setInterval(reload, REFRESH_MS);
    return () => clearInterval(t);
  }, [reload]);

  const saveRetention = async () => {
    setSavingRetention(true);
    try {
      await api.put("/admin/activity/settings", { days: parseInt(editRetention || "5") });
      toast.success(`Retention set to ${editRetention} day(s).`);
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingRetention(false); }
  };

  if (!live || !daily) return <div className="text-muted-foreground" data-testid="admin-activity-loading">Loading live activity…</div>;

  return (
    <div className="space-y-6" data-testid="admin-activity-panel">
      {/* Stats row */}
      <div className="grid sm:grid-cols-4 gap-4">
        <Stat icon={Pulse} label="Live now (last 2 min)" value={live.live_count} testid="admin-act-live-count" tone="primary" />
        <Stat icon={Users} label={`Active users · last ${daily.days}d`} value={daily.per_user.length} testid="admin-act-users" />
        <Stat icon={Clock} label={`Total events · last ${daily.days}d`} value={daily.total_events} testid="admin-act-events" />
        <Stat icon={Clock} label="Retention" value={`${retentionDays} day${retentionDays === 1 ? "" : "s"}`} testid="admin-act-retention" />
      </div>

      {/* Retention setting */}
      <div className="card-flat p-5">
        <div className="flex flex-wrap items-end gap-4">
          <label className="text-sm">
            <span className="block text-xs text-muted-foreground mb-1">Daily-log retention (1–90 days)</span>
            <input
              type="number" min={1} max={90}
              value={editRetention}
              onChange={(e) => setEditRetention(e.target.value)}
              className="w-24 bg-background border hairline px-3 py-2 text-sm"
              data-testid="admin-act-retention-input"
            />
          </label>
          <button
            onClick={saveRetention}
            disabled={savingRetention || parseInt(editRetention) === retentionDays}
            className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="admin-act-retention-save"
          >
            <FloppyDisk size={14} weight="duotone" /> {savingRetention ? "Saving…" : "Save retention"}
          </button>
          <p className="text-xs text-muted-foreground flex-1 min-w-[200px]">
            Logs older than this are auto-purged hourly. Default 5 days.
          </p>
        </div>
      </div>

      {/* Live users table */}
      <div className="card-flat p-0 overflow-hidden">
        <div className="px-5 py-3 border-b hairline flex items-center justify-between">
          <h3 className="font-serif text-xl inline-flex items-center gap-2">
            <Pulse size={18} weight="duotone" className="text-[hsl(var(--secondary))]" /> Live users
          </h3>
          <span className="text-xs text-muted-foreground">Refreshes every {Math.round(REFRESH_MS / 1000)}s</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="px-5 py-2">User</th>
                <th className="px-5 py-2">Page</th>
                <th className="px-5 py-2">Feature</th>
                <th className="px-5 py-2">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {live.users.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-8 text-center text-muted-foreground" data-testid="admin-act-live-empty">No one online right now.</td></tr>
              )}
              {live.users.map((u) => (
                <tr key={u.user_id} className="border-t hairline" data-testid={`admin-act-live-row-${u.user_id}`}>
                  <td className="px-5 py-2">
                    <div className="font-medium">{u.name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </td>
                  <td className="px-5 py-2 font-mono text-xs">{u.page_path || "—"}</td>
                  <td className="px-5 py-2">{u.feature_slug || "—"}</td>
                  <td className="px-5 py-2 text-xs text-muted-foreground">{fmt(u.last_seen)} · {ago(u.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-user (rolled up) */}
      <div className="card-flat p-0 overflow-hidden">
        <div className="px-5 py-3 border-b hairline">
          <h3 className="font-serif text-xl">Per-user usage · last {daily.days} day{daily.days === 1 ? "" : "s"}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="px-5 py-2">User</th>
                <th className="px-5 py-2">Events</th>
                <th className="px-5 py-2">Features touched</th>
                <th className="px-5 py-2">Last active</th>
              </tr>
            </thead>
            <tbody>
              {daily.per_user.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-8 text-center text-muted-foreground">No activity in this window.</td></tr>
              )}
              {daily.per_user.slice(0, 50).map((u) => (
                <tr key={u.user_id} className="border-t hairline" data-testid={`admin-act-user-row-${u.user_id}`}>
                  <td className="px-5 py-2">
                    <div className="font-medium">{u.name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </td>
                  <td className="px-5 py-2 num-metric">{u.events}</td>
                  <td className="px-5 py-2 text-xs">{(u.features || []).filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-5 py-2 text-xs text-muted-foreground">{ago(u.last_active)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top features */}
      <div className="card-flat p-0 overflow-hidden">
        <div className="px-5 py-3 border-b hairline">
          <h3 className="font-serif text-xl">Top features · last {daily.days} day{daily.days === 1 ? "" : "s"}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="px-5 py-2">Feature</th>
                <th className="px-5 py-2">Events</th>
                <th className="px-5 py-2">Unique users</th>
              </tr>
            </thead>
            <tbody>
              {daily.top_features.map((f) => (
                <tr key={f.feature} className="border-t hairline" data-testid={`admin-act-feat-${f.feature}`}>
                  <td className="px-5 py-2 font-mono text-xs">{f.feature}</td>
                  <td className="px-5 py-2 num-metric">{f.events}</td>
                  <td className="px-5 py-2 num-metric">{f.unique_users}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid, tone }) {
  const accent = tone === "primary" ? "text-[hsl(var(--secondary))]" : "text-foreground";
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
        <Icon size={14} weight="duotone" /> <span>{label}</span>
      </div>
      <div className={`num-metric text-3xl ${accent}`}>{value}</div>
    </div>
  );
}
